import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function BundleReview() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({ decision: "approved", counsel_name: "", counsel_signature: "", notes: "" });
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setErr("Missing token"); return; }
    axios.get(`${BASE}/api/public/review/bundle?token=${encodeURIComponent(token)}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.detail || "Invalid or expired token"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await axios.post(`${BASE}/api/public/review/bundle/decide`, { ...form, token });
      setDone(r.data);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  if (err) return <ErrorPanel msg={err}/>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-[12px]">Loading bundle…</div>;

  if (done) {
    return (
      <div className="min-h-screen bg-[var(--span-surface-2)] flex items-center justify-center p-6">
        <div className="card-flat p-8 max-w-md text-center" data-testid="bundle-review-done">
          <div className="text-emerald-600 text-3xl mb-2">✓</div>
          <div className="font-display font-bold text-xl mb-1">Decision recorded</div>
          <div className="text-[12px] text-[var(--span-text-2)]">Thank you. Your verdict ({done.decision}) has been logged on the case. You may close this tab.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--span-surface-2)] py-8 px-4">
      <div className="max-w-3xl mx-auto" data-testid="bundle-review-page">
        <div className="flex items-center gap-2 mb-4">
          <img src="/api/static/brand/C2_Horizontal_White.svg" alt="SPAN CasePrime" className="h-8" style={{ width: "220px", height: "auto" }}/>
          <div>
            <div className="font-display font-bold text-lg">External Counsel Review</div>
            <div className="text-[11px] text-[var(--span-text-2)]">No SPAN login required · token expires 72h after issue · single-use</div>
          </div>
        </div>

        <div className="card-flat p-5 mb-4">
          <div className="text-[10px] uppercase tracking-wider text-[var(--span-text-2)]">Case under review</div>
          <div className="font-display font-bold mt-1">{data.case?.case_code} — {data.case?.title}</div>
          <div className="text-[12px] mt-1 text-[var(--span-text-2)]">
            Client: <b>{data.client?.name || "—"}</b> · Country: {data.case?.country} · Value: USD {(data.case?.value_usd || 0).toLocaleString()}
          </div>
          <div className="text-[12px] mt-2">{data.case?.description}</div>
          <div className="text-[11px] mt-3 hairline p-2 bg-[var(--span-surface-2)] font-mono">
            Variant: {data.vetting?.country_variant} · Requested {new Date(data.vetting?.requested_at).toLocaleString()} · Counsel kind: {data.vetting?.counsel_kind}
          </div>
          <a data-testid="bundle-pdf-link" href={`${BASE}${data.bundle_pdf_url}`} target="_blank" rel="noopener" className="inline-block mt-3 text-[11px] hairline px-3 py-1.5 hover:bg-[var(--span-surface-3)]">
            ↗ Open court bundle PDF
          </a>
        </div>

        <form onSubmit={submit} className="card-flat p-5 space-y-3" data-testid="bundle-decision-form">
          <div className="font-display font-bold">Your decision</div>
          <div className="grid grid-cols-3 gap-2 text-[12px]">
            {["approved", "changes_requested", "rejected"].map(d => (
              <label key={d} className={`hairline px-3 py-2 cursor-pointer text-center ${form.decision===d ? "bg-emerald-50 border-emerald-400" : ""}`}>
                <input type="radio" name="decision" value={d} checked={form.decision===d} onChange={e=>setForm({...form, decision:e.target.value})} className="sr-only"/>
                {d.replace("_", " ")}
              </label>
            ))}
          </div>
          <input data-testid="counsel-name" required placeholder="Your name *" value={form.counsel_name} onChange={e=>setForm({...form, counsel_name:e.target.value})} className="w-full hairline px-2 py-1.5 text-[12px]"/>
          <input data-testid="counsel-signature" placeholder="Signature / Bar Council No. (optional)" value={form.counsel_signature} onChange={e=>setForm({...form, counsel_signature:e.target.value})} className="w-full hairline px-2 py-1.5 text-[12px]"/>
          <textarea data-testid="counsel-notes" placeholder="Notes / suggested revisions" rows={4} value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})} className="w-full hairline px-2 py-1.5 text-[12px]"/>
          <button data-testid="submit-decision" disabled={submitting} className="span-btn">{submitting ? "Submitting…" : "Submit decision"}</button>
        </form>
        <div className="text-[10px] text-[var(--span-text-2)] text-center mt-4">SPAN CONSULTING PRIVATE LIMITED · GSTIN 07AAJCT3137F1ZE</div>
      </div>
    </div>
  );
}

function ErrorPanel({ msg }) {
  return (
    <div className="min-h-screen bg-[var(--span-surface-2)] flex items-center justify-center p-6">
      <div className="card-flat p-6 max-w-md text-center" data-testid="bundle-review-error">
        <div className="text-red-600 text-3xl mb-2">✕</div>
        <div className="font-display font-bold text-lg mb-1">Unable to load review</div>
        <div className="text-[12px] text-[var(--span-text-2)]">{msg}</div>
        <div className="text-[10px] text-[var(--span-text-2)] mt-4">Please contact your SPAN point of contact for a fresh link.</div>
      </div>
    </div>
  );
}
